// The status codes the API uses, and what each one means here (spec/08-api-design.md §2.3, §2.6).
//
// `404` is returned instead of `403` wherever revealing that something exists would leak — a venue
// in another organisation, for instance. That is a rule about which entry to pick, not about this
// table.
export const HTTP_STATUS = {
    ok: 200,
    created: 201,
    accepted: 202,
    noContent: 204,
    /** Validation: the request could not be understood as written. */
    badRequest: 400,
    /** Unauthenticated: no principal could be resolved. */
    unauthorized: 401,
    /** Payment or entitlement required. */
    paymentRequired: 402,
    /** Authenticated, but not allowed. */
    forbidden: 403,
    /** Not found, or hidden because saying so would leak. */
    notFound: 404,
    /** Conflict with the current state. */
    conflict: 409,
    /** Stale: the `If-Match` ETag no longer matches. */
    preconditionFailed: 412,
    /** Understood, well-formed, but semantically impossible. */
    unprocessableEntity: 422,
    /** Rate limited. Carries `Retry-After` and the `X-RateLimit-*` headers. */
    tooManyRequests: 429,
    internalServerError: 500,
    serviceUnavailable: 503,
};
/**
 * Every error status, as the error reference groups the catalogue by it (spec/08 §2.6). A status the
 * catalogue may use has to be here, so a new one cannot appear on the reference without a heading.
 */
export const ERROR_STATUSES = {
    400: { reason: 'Bad Request', meaning: 'The request could not be understood as written, and has to change before it is sent again.' },
    401: { reason: 'Unauthorized', meaning: 'The request came with no session, token or API key the API accepts.' },
    402: {
        reason: 'Payment Required',
        meaning: 'Something has to be paid for first: a session’s funding, or a capability the organisation’s plan does not include.',
    },
    403: { reason: 'Forbidden', meaning: 'The caller is known, and may not do this: its role, a rule of the venue or a ban stops it.' },
    404: {
        reason: 'Not Found',
        meaning: 'Nothing the caller may see is there. It is also the answer wherever saying that something exists would reveal it.',
    },
    409: { reason: 'Conflict', meaning: 'The request conflicts with the current state: something changed first, or is already so.' },
    412: { reason: 'Precondition Failed', meaning: 'The update was based on a version of the resource that is no longer current.' },
    422: { reason: 'Unprocessable Content', meaning: 'The request is well formed, and cannot be carried out as asked.' },
    429: { reason: 'Too Many Requests', meaning: 'Too many requests in the current window. `Retry-After` says how long to wait.' },
    500: { reason: 'Internal Server Error', meaning: 'The API failed, and nothing the request would have changed was kept.' },
    503: { reason: 'Service Unavailable', meaning: 'A service the API depends on cannot do its part right now.' },
};
//# sourceMappingURL=status.js.map